import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get } from 'lodash'
import { Field } from 'formik'
import AdminCommonListLayout from 'admin/components/layout/AdminCommonListLayout'
import { ShortId } from 'components/ShortId'
import Timestamp from 'components/Timestamp'
import { adminBeneficiaryVettingList } from './actions/admin_beneficiary_vetting'
import { adminBeneficiaryVettingStates } from '../../actions/admin_dropdown_options'
import { Tooltip } from '@material-ui/core'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import { adminUserList } from '../users/actions/admin_user'
import { FormikDropdownField } from 'components/form/Dropdown'


export default function AdminBeneficiariesVetting(props) {
  const dispatch = useDispatch()
  const { history } = props
  const is_loading = useSelector(() => adminBeneficiaryVettingList.isLoading())
  const STATUS_LABELS = useSelector(() => adminBeneficiaryVettingStates.getAsOptions())

  useEffect(() => {
    dispatch(adminBeneficiaryVettingList.fetchListIfNeeded())
    dispatch(adminBeneficiaryVettingStates.fetchListIfNeeded())
    dispatch(adminUserList.fetchListIfNeeded())
  }, [dispatch])

  const onAddBeneficiary = () => {
    history.push('/admin/beneficiary_vetting')
  }

  const onEditBeneficiary = (beneficiary_id) => {
    history.push('/admin/beneficiary_vetting/' + beneficiary_id)
  }

  const onUpdateListOrdering = (field) => {
    dispatch(adminBeneficiaryVettingList.updateListOrdering(field))
  }

  const renderBeneficiaryStatus = (status) => {
    var status_color = ''
    switch (status) {
      case 'Approved':
        status_color = 'green'
        break
      case 'Vetting in Progress':
        status_color = 'orange'
        break
      case 'Declined':
        status_color = 'red'
        break
    }
    return (<Tooltip arrow title={status}><FiberManualRecordIcon style={{ color: status_color }} /></Tooltip>)
  }

  const statusFilterOptions = [
    { value: 'all', label: 'All'},
    { value: 'vetting_in_progress', label: 'Vetting in Progress' },
    { value: 'declined', label: 'Declined' }
  ]

  const renderAdditionalFilter = (formik_props) => (
      <div style={{ marginTop: 8 }}>
        <FormikDropdownField
          name="status"
          options={statusFilterOptions}
          formik_props={formik_props}
          label="Status Filter"
          size="medium"
        />
      </div>
  )

  const columnsDesktop = [
    {
      field: 'id',
      title: 'ID',
      render: (item) => <ShortId value={item.id} />,
      visible: false
    },
    {
      field: 'company_legal_name',
      title: 'Organisation',
      render: (item) => (
        <a onClick={() => onEditBeneficiary(item.id)}>{get(item, 'company_legal_name', '')}</a>
      ),
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'company_legal_name' : '-company_legal_name'),
      visible: true
    },
    {
      field: 'company_website',
      title: 'Website',
      render: (item) => get(item, 'company_website', '') || '--',
      visible: true
    },
    {
      field: 'status',
      title: 'Status',
      render: (item) => renderBeneficiaryStatus(item.status ? STATUS_LABELS.find((option) => option.value === item.status)?.label : '--'),
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'status' : '-status'),
      visible: true
    },
    {
      field: 'is_registered_npo',
      title: 'Registered NPO',
      render: (item) => get(item, 'is_registered_npo', '') ? 'Yes' : 'No',
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'is_registered_npo' : '-is_registered_npo'),
      visible: false
    },
    {
      field: 'is_registered_dsd',
      title: 'Registered with DSD',
      render: (item) => get(item, 'is_registered_dsd', '') ? 'Yes' : 'No',
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'is_registered_dsd' : '-is_registered_dsd'),
      visible: false
    },
    {
      field: 'created_at',
      title: 'Created at',
      render: (item) => (item.created_at ? <Timestamp value={item.created_at} /> : '--'),
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'created_at' : '-created_at'),
      visible: true
    },
    {
      field: 'modified_at',
      title: 'Modified at',
      render: (item) => (item.modified_at ? <Timestamp value={item.modified_at} /> : '--'),
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'modified_at' : '-modified_at'),
      visible: false
    },
    {
      field: 'created_by_user',
      title: 'Created by',
      render: (item) => get(item, 'created_by_user_display_name', '--'),
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'created_by_user_display_name' : '-created_by_user_display_name'),
      visible: false
    },
    {
      field: 'modified_by_user',
      title: 'Modified by',
      render: (item) => get(item, 'modified_by_user_display_name', '--'),
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'modified_by_user_display_name' : '-modified_by_user_display_name'),
      visible: false
    },
    {
      field: 'approved_at', 
      title: 'Approval Date', 
      render: (item) => item.approved_at ? <Timestamp value={item.approved_at} /> : '--', 
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'approved_at' : '-approved_at'), 
      visible: false
    },
    {
      field: 'approved_by_user',
      title: 'Approved by',
      render: (item) => get(item, 'approved_by_user_display_name', '--'),
      sort: (direction) => onUpdateListOrdering(direction === 'asc' ? 'approved_by_user_display_name' : '-approved_by_user_display_name'),
      visible: false
    }
  ]

  return (
    <AdminCommonListLayout
      active_key="beneficiary_vetting"
      breadcrumbs={[
        { name: 'admin_home' },
        { name: 'beneficiary_vetting', label: 'Beneficiary Vetting', url: '/admin/beneficiaries_vetting' }
      ]}
      title="Beneficiary Vetting"
      add_label="New Beneficiary for Vetting"
      onAddRow={onAddBeneficiary}
      onEditRow={onEditBeneficiary}
      is_loading={is_loading}
      columns={columnsDesktop}
      enableAnyFieldFilter={true}
      renderAdditionalFilter={renderAdditionalFilter}
      item_list={adminBeneficiaryVettingList}
      useColumnsSelector={true}
      canDelete={false}
      storeTableName="adminBeneficiaryVettingList"
    />
  )
}